import * as XLSX from 'xlsx';

const ARCOS_TITLE = 'ARCOS — Advanced Request Communication & Operations System';

function formatExportDate(): string {
  const d = new Date();
  return d.toLocaleString('en-GB', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
  }) + ' UTC';
}

/**
 * Export data to Excel with ARCOS title block.
 * Rows 1-3 = title/subtitle/generated info, row 4 = blank, row 5 = headers, row 6+ = data
 */
export const exportToExcel = (
  data: Record<string, unknown>[],
  filename: string,
  sheetName: string = 'Sheet1',
  options?: { title?: string; subtitle?: string }
) => {
  if (data.length === 0) return;

  const headers = Object.keys(data[0]);
  const title = options?.title || sheetName;
  const subtitle = options?.subtitle || `${data.length} record(s)`;

  const aoa: (string | number | null | undefined)[][] = [
    [ARCOS_TITLE],
    [title],
    [`Generated on: ${formatExportDate()} | ${subtitle}`],
    [],
    headers,
    ...data.map(row => headers.map(h => {
      const v = row[h];
      return v == null ? '' : v;
    }) as (string | number | null | undefined)[]),
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Auto-size columns
  const maxWidth = 50;
  const colWidths = headers.map((_key, i) => {
    const maxLength = Math.max(
      headers[i].length,
      ...data.map(row => String(row[headers[i]] ?? '').length)
    );
    return { wch: Math.min(maxLength + 2, maxWidth) };
  });
  ws['!cols'] = colWidths;

  // Merge title rows across all columns
  const colCount = headers.length;
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: colCount - 1 } },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

/**
 * Export array-of-arrays to Excel with ARCOS title block.
 * headers = column names, rows = data arrays matching header order.
 */
export const exportAoaToExcel = (
  headers: string[],
  rows: (string | number | null | undefined)[][],
  filename: string,
  sheetName: string,
  options?: { title?: string; subtitle?: string; colWidths?: { wch: number }[] }
) => {
  const title = options?.title || sheetName;
  const subtitle = options?.subtitle || `${rows.length} record(s)`;

  const aoa: (string | number | null | undefined)[][] = [
    [ARCOS_TITLE],
    [title],
    [`Generated on: ${formatExportDate()} | ${subtitle}`],
    [],
    headers,
    ...rows,
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Column widths
  if (options?.colWidths) {
    ws['!cols'] = options.colWidths;
  } else {
    const maxWidth = 50;
    ws['!cols'] = headers.map((_h, i) => {
      const maxLen = Math.max(
        headers[i].length,
        ...rows.map(r => String(r[i] ?? '').length)
      );
      return { wch: Math.min(maxLen + 2, maxWidth) };
    });
  }

  // Merge title rows
  const colCount = headers.length;
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: colCount - 1 } },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

/**
 * Export data with multiple sheets to Excel file (with title block per sheet)
 */
export const exportMultipleSheetsToExcel = (
  sheets: Array<{ data: Record<string, unknown>[]; name: string; title?: string; subtitle?: string }>,
  filename: string
) => {
  const wb = XLSX.utils.book_new();

  sheets.forEach(sheet => {
    if (sheet.data.length === 0) return;

    const headers = Object.keys(sheet.data[0]);
    const title = sheet.title || sheet.name;
    const subtitle = sheet.subtitle || `${sheet.data.length} record(s)`;

    const aoa: (string | number | null | undefined)[][] = [
      [ARCOS_TITLE],
      [title],
      [`Generated on: ${formatExportDate()} | ${subtitle}`],
      [],
      headers,
      ...sheet.data.map(row => headers.map(h => {
        const v = row[h];
        return v == null ? '' : v;
      }) as (string | number | null | undefined)[]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    const maxWidth = 50;
    if (sheet.data.length > 0) {
      ws['!cols'] = headers.map((_key, i) => {
        const maxLength = Math.max(
          headers[i].length,
          ...sheet.data.map(row => String(row[headers[i]] ?? '').length)
        );
        return { wch: Math.min(maxLength + 2, maxWidth) };
      });
    }

    const colCount = headers.length;
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: colCount - 1 } },
    ];

    XLSX.utils.book_append_sheet(wb, ws, sheet.name);
  });

  XLSX.writeFile(wb, `${filename}.xlsx`);
};

/**
 * Parse an Excel file and extract Patient IDs for offer import.
 * Looks for a column named "Patient ID" (case-insensitive) and reads all non-empty values.
 * Skips ARCOS title rows (rows 1-4) automatically.
 * Returns array of patient ID strings found in the file.
 */
export const parsePatientIdsFromExcel = (buffer: ArrayBuffer): { patientIds: string[]; sheetName: string } => {
  const wb = XLSX.read(buffer, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  if (!ws) return { patientIds: [], sheetName: '' };

  const rawData = XLSX.utils.sheet_to_json(ws, { header: 1 }) as unknown as unknown[][];

  // Find the header row (contains "Patient ID" column)
  let headerRowIndex = -1;
  let patientIdColIndex = -1;
  for (let i = 0; i < Math.min(rawData.length, 10); i++) {
    const row = rawData[i];
    if (!Array.isArray(row)) continue;
    const colIdx = row.findIndex(cell =>
      typeof cell === 'string' && cell.trim().toLowerCase().replace(/\s+/g, '') === 'patientid'
    );
    if (colIdx >= 0) {
      headerRowIndex = i;
      patientIdColIndex = colIdx;
      break;
    }
  }

  if (headerRowIndex < 0 || patientIdColIndex < 0) {
    return { patientIds: [], sheetName };
  }

  // Extract patient IDs from rows below header
  const patientIds: string[] = [];
  for (let i = headerRowIndex + 1; i < rawData.length; i++) {
    const row = rawData[i];
    if (!Array.isArray(row)) continue;
    const cellValue = row[patientIdColIndex];
    if (cellValue != null) {
      const id = String(cellValue).trim();
      if (id.length > 0) {
        patientIds.push(id);
      }
    }
  }

  return { patientIds, sheetName };
};

/**
 * Format date for Excel export
 */
export const formatDateForExcel = (date: string | Date): string => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};
